import {
    styled,
    Box,
    Grid,
    Paper,
    Container,
    Typography,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    SelectChangeEvent,
    TextField,
    Tabs,
    Tab,
} from '@mui/material';

import React, { useState, useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import { GeneralConstants } from 'app/constants/general-constants';

import { Metrics } from 'app/enum/metrics';
import { KpiMetricsData } from 'app/model/tools/kpi-metrics-data';
import { dashBoardService } from 'app/services/tools/dashboard.service';

import { KpiChart } from './kpi-chart';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const CustomTabPanel = (props: TabPanelProps): React.ReactElement => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role='tabpanel'
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
};

const a11yProps = (index: number): { id: string; ariaControls: string } => {
    return {
        id: `simple-tab-${index}`,
        ariaControls: `simple-tabpanel-${index}`,
    };
};

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: 'dark' === theme.palette.mode ? '#1A2027' : '#fff',
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
}));

const dateToStr = (d: Date): string => {
    // eslint-disable-next-line
    return `${d.getUTCFullYear()}-${`0${d.getUTCMonth() + 1}`.slice(-2)}-${`0${d.getUTCDate()}`.slice(-2)}`;
};

interface StatMap {
    xaxis: string[];
    yaxis: number[];
}

interface KpiStat {
    title: string;
    labels: string[];
    values: number[];
}

interface LineChartProps {
    startDate: string;
    endDate: string;
    kpiType: string;
    kpiEnv: string;
}
const KpiLineChart: React.FunctionComponent<LineChartProps> = (props: LineChartProps) => {
    const { startDate, endDate, kpiType, kpiEnv } = props;
    // eslint-disable-next-line prefer-const
    let [statistics, setStatistics] = useState<KpiMetricsData[]>();
    const { t } = useTranslation();
    useEffect(() => {
        const statList = dashBoardService
            .listAll({
                queryFilters: { dateFrom: startDate, dateTo: endDate },
                pageSize: GeneralConstants.PAGE_SIZE_HUGE,
                sort: { 'spec.date': 'asc' },
            })
            .subscribe(setStatistics);

        return () => {
            statList.unsubscribe();
        };
    }, [startDate, endDate]);
    const dataMap = new Map<string, StatMap>();
    const chartStats = [] as KpiStat[];
    if (!statistics) {
        return <h1>Loading....</h1>;
    }

    for (const stat of Object.values(statistics)) {
        const statData = stat as unknown as KpiMetricsData;
        let sMap: StatMap | undefined = dataMap.get(statData.type);
        const xval = dateToStr(new Date(statData.date));
        const yval = parseInt(statData.value, 10);
        if (sMap !== undefined) {
            sMap.xaxis.push(xval);
            sMap.yaxis.push(yval);
        } else {
            sMap = { xaxis: [xval], yaxis: [yval] };
        }
        dataMap.set(stat.type, sMap);
    }
    Object.keys(Metrics).forEach((type) => {
        if (!type.startsWith(kpiType)) {
            return;
        }
        if ('stage' === kpiEnv && !type.endsWith('_STAGE')) {
            return;
        }
        if ('prod' === kpiEnv && type.endsWith('_STAGE')) {
            return;
        }

        const sMap = dataMap.get(type);
        if (!sMap) {
            chartStats.push({
                title: t(`forms:Metrics.${type}`),
                labels: [],
                values: [],
            });
        } else {
            chartStats.push({
                title: t(`forms:Metrics.${type}`),
                labels: sMap.xaxis,
                values: sMap.yaxis,
            });
        }
    });

    const HUNDRED = 100;
    const NINE_HUNDRED = 900;

    return (
        <Box sx={{ display: 'flex' }}>
            <Box
                component='main'
                sx={{
                    backgroundColor: (theme) =>
                        'light' === theme.palette.mode ? theme.palette.grey[HUNDRED] : theme.palette.grey[NINE_HUNDRED],
                    flexGrow: 1,
                    overflow: 'auto',
                }}
            >
                <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
                    <Grid container spacing={3}>
                        {/* KpiChart */}
                        {Object.keys(chartStats).map((key) => {
                            const idx = parseInt(key, 10);
                            const labels = chartStats[idx].labels;
                            const values = chartStats[idx].values;
                            const title = chartStats[idx].title;

                            return (
                                <Grid item xs={12} md={6} lg={4} key={key}>
                                    <Paper
                                        sx={{
                                            p: 2,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            minHeight: 240,
                                        }}
                                    >
                                        <KpiChart title={title} xdata={labels} ydata={values} />
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Container>
            </Box>
        </Box>
    );
};
const KpiTabs = (): React.ReactElement => {
    const THIRTY = 30;
    const endDate = new Date();
    const startDate = new Date(new Date().setDate(endDate.getDate() - THIRTY));

    const [env, setEnv] = React.useState('stage');
    const [kpiType, setKpiType] = React.useState('ANALOG');
    const [dateFrom, setDateFrom] = React.useState(dateToStr(startDate));
    const [dateTo, setDateTo] = React.useState(dateToStr(endDate));
    const [tabNum, setTabNum] = React.useState(0);

    const kpiTypes = ['ANALOG', 'SEAM'];
    const handleTabChange = (event: React.SyntheticEvent, newValue: number): void => {
        setTabNum(newValue);
        setKpiType(kpiTypes[newValue]);
    };
    const envChange = (event: SelectChangeEvent): void => {
        setEnv(event.target.value);
    };
    const handleDateFromChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setDateFrom(event.target.value);
    };
    const handleDateToChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setDateTo(event.target.value);
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabNum} onChange={handleTabChange}>
                    <Tab label='Analog KPI' {...a11yProps(0)} />
                    <Tab label='Seam KPI' {...a11yProps(1)} />
                </Tabs>
            </Box>
            <CustomTabPanel value={tabNum} index={0}>
                <Grid container spacing={3}>
                    <Grid item xs={4}>
                        <Item>
                            <Box sx={{ maxWidth: 120 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Environment</InputLabel>
                                    <Select value={env} label='Environment' onChange={envChange}>
                                        <MenuItem value='prod'>Production</MenuItem>
                                        <MenuItem value='stage'>Staging</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        </Item>
                    </Grid>
                    <Grid item xs={4}>
                        <Item>
                            <Box sx={{ maxWidth: 120 }}>
                                <TextField
                                    fullWidth
                                    name='dateFrom'
                                    label='Filter Date From'
                                    type='date'
                                    InputLabelProps={{ shrink: true, required: true }}
                                    value={dateFrom}
                                    onChange={handleDateFromChange}
                                />
                            </Box>
                        </Item>
                    </Grid>
                    <Grid item xs={4}>
                        <Item>
                            <Box sx={{ maxWidth: 120 }}>
                                <TextField
                                    fullWidth
                                    name='dateTo'
                                    label='Filter Date To'
                                    type='date'
                                    InputLabelProps={{ shrink: true, required: true }}
                                    value={dateTo}
                                    onChange={handleDateToChange}
                                />
                            </Box>
                        </Item>
                    </Grid>
                    <Grid item xs={12}>
                        <KpiLineChart startDate={dateFrom} endDate={dateTo} kpiType={kpiType} kpiEnv={env} />
                    </Grid>
                </Grid>
            </CustomTabPanel>
            <CustomTabPanel value={tabNum} index={1}>
                <Grid container spacing={3}>
                    <Grid item xs={4}>
                        <Item>
                            <Box sx={{ maxWidth: 120 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Environment</InputLabel>
                                    <Select value={env} label='Environment' onChange={envChange}>
                                        <MenuItem value='prod'>Production</MenuItem>
                                        <MenuItem value='stage'>Staging</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        </Item>
                    </Grid>
                    <Grid item xs={4}>
                        <Item>
                            <Box sx={{ maxWidth: 120 }}>
                                <TextField
                                    fullWidth
                                    name='dateFrom'
                                    label='Filter Date From'
                                    type='date'
                                    InputLabelProps={{ shrink: true, required: true }}
                                    value={dateFrom}
                                    onChange={handleDateFromChange}
                                />
                            </Box>
                        </Item>
                    </Grid>
                    <Grid item xs={4}>
                        <Item>
                            <Box sx={{ maxWidth: 120 }}>
                                <TextField
                                    fullWidth
                                    name='dateTo'
                                    label='Filter Date To'
                                    type='date'
                                    InputLabelProps={{ shrink: true, required: true }}
                                    value={dateTo}
                                    onChange={handleDateToChange}
                                />
                            </Box>
                        </Item>
                    </Grid>
                    <Grid item xs={12}>
                        <KpiLineChart startDate={dateFrom} endDate={dateTo} kpiType={kpiType} kpiEnv={env} />
                    </Grid>
                </Grid>
            </CustomTabPanel>
        </Box>
    );
};

export const KpiDashboard: React.FunctionComponent = () => {
    return <KpiTabs />;
};
/* eslint max-lines: off */
